import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Plus, X } from "lucide-react";

const initial = { name: "", email: "", phone: "", company: "", notes: "" };

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim() ? v.trim() : null])
      );
      payload.name = form.name.trim();
      await api.post("/clients", payload);
      setForm(initial);
      setShowNew(false);
      load();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8]">Onboarded</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Clients</h1>
          <p className="text-[#bdb6c4] mt-2">People you're actively building for.</p>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          data-testid="new-client-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors"
        >
          {showNew ? <X size={14} /> : <Plus size={14} />}
          {showNew ? "Cancel" : "New client"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={onCreate} data-testid="new-client-form" className="mt-6 bg-[#13121f] border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="client-name" required />
            <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="client-email" type="email" />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="client-phone" />
            <Input label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="client-company" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8] block mb-2">Notes</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="client-notes"
              className="w-full bg-[#0a0a14] border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#f5c76e]/60 transition-colors resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !form.name.trim()}
            data-testid="create-client-button"
            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-[#f5c76e] text-[#0a0a14] font-semibold text-sm hover:bg-[#ffd485] transition-colors disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create client"}
          </button>
        </form>
      )}

      <div className="mt-8 space-y-2" data-testid="clients-list">
        {loading ? (
          <p className="text-[#9c95a8] text-sm">Loading…</p>
        ) : clients.length === 0 ? (
          <p className="text-[#9c95a8] text-sm">No clients yet. Convert a lead, or add one above.</p>
        ) : (
          clients.map((c) => (
            <Link
              key={c.id}
              to={`/admin/clients/${c.id}`}
              className="block bg-[#13121f] border border-white/10 rounded-xl px-5 py-4 hover:border-[#f5c76e]/30 transition-colors"
            >
              <div className="flex justify-between items-center gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{c.name}</p>
                  <p className="text-sm text-[#9c95a8] truncate">
                    {[c.company, c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
                <span className="font-mono text-[10px] text-[#6b6677]">
                  {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, testid, type = "text", required }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8] block mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testid}
      className="w-full bg-[#0a0a14] border border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#f5c76e]/60 transition-colors"
    />
  </div>
);

export default Clients;
